import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export interface ProgressLoaderConfig {
  isVisible: boolean;
  title: string;
  message: string;
  progress: number;
  totalItems: number;
  estimatedTimeRemaining: number;
  initialEstimatedTime: number;
  showProgressBar?: boolean;
  showTimeRemaining?: boolean;
  showItemCount?: boolean;
}

@Component({
  selector: 'app-progress-loader',
  template: `
    @if (config.isVisible) {
      <div>
        <div class="loading-content">
          <div class="loading-spinner">
            <i class="pi pi-spin pi-spinner"></i>
          </div>
          <h4>{{ config.title | translate }}</h4>
          <p class="loading-message">
            {{ config.message | translate }}
            @if (config.showItemCount && config.totalItems > 0) {
              <span>
                {{ config.totalItems }} {{ 'ITEMS' | translate }}
              </span>
            }
            @if (config.showTimeRemaining && config.initialEstimatedTime > 0) {
              <span
                >
                {{ getFormattedTimeRemaining() }}
              </span>
            }
          </p>
          @if (config.showProgressBar) {
            <div class="progress-container">
              <div class="progress-bar">
                <div class="progress-fill" [style.width.%]="config.progress"></div>
              </div>
              <div class="progress-text">{{ getProgressPercentage() }}%</div>
            </div>
          }
        </div>
        <div class="loading-overlay"></div>
      </div>
    }
    `,
  styles: [
    `
      .loading-overlay {
        background-color: rgb(93, 85, 85);
        position: fixed;
        left: 0;
        top: 0;
        height: 100%;
        width: 100%;
        opacity: 0.5;
        z-index: 888 !important;
      }

      .loading-content {
        background-color: rgba(255, 255, 255, 1);
        padding: 2rem;
        border-radius: 0.5rem;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.8);
        text-align: center;
        width: 100%;
        max-width: 30vw;
        min-width: 300px;
        position: absolute !important;
        z-index: 999;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      .loading-spinner {
        margin-bottom: 1rem;
        color: var(--color-primary-500);
      }

      h4 {
        margin-bottom: 1rem;
        color: #000000;
        font-size: 1.25rem;
        font-weight: 700;
      }

      .loading-message {
        margin-bottom: 1.5rem;
        color: #000000;
        line-height: 1.5;
        font-size: 0.95rem;
        font-weight: 600;
      }

      .progress-bar {
        width: 100%;
        height: 8px;
        background-color: var(--color-gray-200);
        border-radius: 4px;
        overflow: hidden;
        margin-bottom: 0.5rem;
      }

      .progress-fill {
        height: 100%;
        background-color: var(--color-primary-500);
        border-radius: 4px;
        transition: width 0.3s ease;
      }

      .progress-text {
        font-size: 0.875rem;
        color: #000000;
        font-weight: 700;
      }
    `],
  standalone: false
})
export class ProgressLoaderComponent implements OnInit, OnDestroy, OnChanges {
  @Input() config: ProgressLoaderConfig = {
    isVisible: false,
    title: '',
    message: '',
    progress: 0,
    totalItems: 0,
    estimatedTimeRemaining: 0,
    initialEstimatedTime: 0,
    showProgressBar: true,
    showTimeRemaining: true,
    showItemCount: true
  };

  @Output() onCancel = new EventEmitter<void>();

  private progressInterval: any;

  constructor(private translate: TranslateService) {}

  ngOnInit() {
    if (this.config.isVisible && this.config.showProgressBar) {
      this.startProgressSimulation();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['config'] && this.config.isVisible && this.config.showProgressBar && !this.progressInterval) {
      this.startProgressSimulation();
    }
    
    if (changes['config'] && !this.config.isVisible && this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  ngOnDestroy() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
    }
  }

  /**
   * Start progress simulation with realistic slowdown
   */
  private startProgressSimulation() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
    
    const totalTime = this.config.initialEstimatedTime * 1000; // Convert to milliseconds
    const interval = 100; // Update every 100ms
    const baseIncrement = 100 / (totalTime / interval);

    this.progressInterval = setInterval(() => {
      if (this.config.progress < 95) {
        // Stop at 95% to wait for actual completion
        let increment = baseIncrement;

        // Slow down after 80%
        if (this.config.progress >= 80) {
          increment = baseIncrement * 0.3; // 70% slower
        }

        // Even slower after 90%
        if (this.config.progress >= 90) {
          increment = baseIncrement * 0.1; // 90% slower
        }

        this.config.progress += increment;

        // Ensure we don't exceed 95%
        if (this.config.progress > 95) {
          this.config.progress = 95;
        }
      }

      if (this.config.progress >= 95 || !this.config.isVisible) {
        clearInterval(this.progressInterval);
        this.progressInterval = null;
      }
    }, interval);
  }

  /**
   * Complete the progress bar smoothly to 100% and then hide
   */
  completeProgress() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }

    // If progress is already at 95% or higher, just complete it quickly
    if (this.config.progress >= 95) {
      this.config.progress = 100;
      // Hide after a short delay to show the completed state
      setTimeout(() => {
        this.config.isVisible = false;
      }, 500);
    } else {
      // If progress is lower, animate it to 100% smoothly
      const remainingProgress = 100 - this.config.progress;
      const steps = 20; // Number of steps to complete
      const stepSize = remainingProgress / steps;
      const stepInterval = 50; // Update every 50ms for smooth animation

      let currentStep = 0;
      const completionInterval = setInterval(() => {
        currentStep++;
        this.config.progress += stepSize;

        if (currentStep >= steps || this.config.progress >= 100) {
          this.config.progress = 100;
          clearInterval(completionInterval);
          
          // Hide after a short delay to show the completed state
          setTimeout(() => {
            this.config.isVisible = false;
          }, 500);
        }
      }, stepInterval);
    }
  }

  /**
   * Get formatted time remaining for progress
   */
  getFormattedTimeRemaining(): string {
    if (this.config.initialEstimatedTime < 60) {
      return ( Math.ceil(this.config.initialEstimatedTime) + ' ' + this.translate.instant('SECONDS'));
    } else {
      const minutes = Math.floor(this.config.initialEstimatedTime / 60);
      const seconds = Math.ceil(this.config.initialEstimatedTime % 60);
      return (
        minutes +
        ' ' + this.translate.instant('MINUTES') +
        (minutes > 1 ? 's' : '') +
        ' ' +
        seconds +
        ' ' + this.translate.instant('SECONDS') +
        (seconds > 1 ? 's' : '')
      );
    }
  }

  /**
   * Handle cancel button click
   */
  onCancelClick() {
    this.onCancel.emit();
  }

  /**
   * Get progress percentage for display
   */
  getProgressPercentage(): number {
    return Math.round(this.config.progress);
  }
}
